// React binding of the uploader: one Uploader per component (useUploader for any transport and record store, useUpload for source files over the HTTP transport and the browser's paused-upload records), its state read with useSyncExternalStore, a beforeunload warning while bytes are moving, and start, pause, resume, cancel and reset actions.
import { useEffect, useState, useSyncExternalStore } from "react";

import type { SourceFileOut } from "@/api/__generated__";

import { browserUploadStore, type UploadRecordStore } from "./store";
import { httpUploadTransport } from "./transport";
import {
  Uploader,
  type StoredFile,
  type UploaderOptions,
  type UploadState,
  type UploadTransport,
} from "./uploader";

export interface UseUploadOptions {
  transport?: UploadTransport;
  records?: UploadRecordStore;
  profile?: boolean;
}

export interface UploadControls<TStored extends StoredFile = SourceFileOut> {
  state: UploadState<TStored>;
  start: (file: File) => void;
  pause: () => void;
  resume: () => void;
  cancel: () => void;
  reset: () => void;
}

const MOVING = new Set(["hashing", "checking", "uploading", "verifying"]);

export function useUploader<TStored extends StoredFile>(
  options: UploaderOptions<TStored>,
): UploadControls<TStored> {
  const [uploader] = useState(() => new Uploader<TStored>(options));
  const state = useSyncExternalStore(uploader.subscribe, uploader.getState, uploader.getState);
  const moving = MOVING.has(state.phase);

  useEffect(() => {
    if (!moving) {
      return undefined;
    }
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => {
      window.removeEventListener("beforeunload", warn);
    };
  }, [moving]);

  useEffect(
    () => () => {
      uploader.pause();
    },
    [uploader],
  );

  return {
    state,
    start: (file) => void uploader.start(file),
    pause: () => {
      uploader.pause();
    },
    resume: () => void uploader.resume(),
    cancel: () => void uploader.cancel(),
    reset: () => {
      uploader.reset();
    },
  };
}

export function useUpload(options: UseUploadOptions = {}): UploadControls {
  return useUploader({
    transport: options.transport ?? httpUploadTransport,
    records: options.records ?? browserUploadStore,
    profile: options.profile ?? true,
  });
}
